// pages/SAPSproPanel.jsx
// Navigator SPRO di dalam view "Config layer (SPRO)" (SAPConfigLayer.jsx).
// Backend: sapSpro.js — semua tampilan membaca CACHE; SAP hanya dibaca saat
// tombol "Pull SPRO from SAP" / "Read from SAP" diklik.
//
// Tiga sub-view:
//   tree    — pohon SPRO (Reference IMG), lazy per node, dengan hitungan
//             tabel/TR/activity yang pernah disentuh TR customizing.
//   objects — tabel/view config → jalur SPRO + t-code IMG.
//   compare — isi tabel config aktual D01 vs Q01 vs P01, per kunci.
//
// Teks UI literal bahasa Inggris (bukan i18n) — pola yang sama dengan tab-tab
// baru lain di dashboard ini.
import { useState, useEffect, useCallback, useRef } from 'react';
import SAPSproGui from './SAPSproGui';

// 2026-10-15: tombol tarik data langsung dari SAP khusus admin (backend juga menolak non-admin).
const isAdminUser = () => { try { return JSON.parse(localStorage.getItem('auth_user') || '{}').role === 'admin'; } catch { return false; } };


const NF = new Intl.NumberFormat('id-ID');
const INP = { padding: '0.38rem 0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12.5, minWidth: 0 };
const TH = { padding: '0.45rem 0.65rem', fontSize: 11.5, fontWeight: 700, color: '#475569', textAlign: 'left',
  background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0 };
const TD = { padding: '0.42rem 0.65rem', fontSize: 12.5, color: '#1e293b', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
const BTN = (primary, busy) => ({
  padding: '0.42rem 0.85rem', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
  border: '1px solid ' + (primary ? 'rgb(var(--brand-600))' : '#cbd5e1'),
  background: primary ? (busy ? 'rgb(var(--brand-50))' : 'rgb(var(--brand-600))') : '#fff',
  color: primary ? (busy ? 'rgb(var(--brand-700))' : '#fff') : '#334155',
  cursor: busy ? 'wait' : 'pointer',
});
const chip = (bg, fg) => ({ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 11,
  fontWeight: 600, background: bg, color: fg, marginLeft: 6, whiteSpace: 'nowrap' });

function Seg({ value, onChange, items }) {
  return (
    <div style={{ display: 'inline-flex', background: 'rgba(241,245,249,0.8)', borderRadius: 14, padding: 4, gap: 2 }}>
      {items.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)}
          style={{ padding: '0.35rem 0.9rem', borderRadius: 10, fontSize: 12.5, fontWeight: 600, border: 'none',
            cursor: 'pointer', background: value === k ? '#fff' : 'transparent',
            color: value === k ? '#0f172a' : '#64748b',
            boxShadow: value === k ? '0 0 0 1px #e2e8f0, 0 1px 2px rgba(0,0,0,0.05)' : 'none' }}>
          {label}
        </button>
      ))}
    </div>
  );
}

function Counts({ n }) {
  const parts = [];
  if (n.node_type !== 'IMG') {
    parts.push(`${NF.format(n.obj_count || 0)} tables`);
    parts.push(`${NF.format(n.act_count || 0)} activities`);
  }
  parts.push(`${NF.format(n.tr_count || 0)} TR`);
  return <span style={{ fontSize: 11.5, color: '#64748b', marginLeft: 8, whiteSpace: 'nowrap' }}>{parts.join(' · ')}</span>;
}

// ── Pohon ───────────────────────────────────────────────────────────────────
function TreeNode({ api, connId, n, touched, onPick, depth }) {
  const [open, setOpen] = useState(false);
  const [kids, setKids] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAct = n.node_type === 'IMG';
  const toggle = async () => {
    if (isAct) { setOpen(o => !o); return; }
    if (!open && kids == null) {
      setBusy(true);
      try {
        const d = await api(`/api/sap/customizing/spro/tree?connectionId=${connId}&parent=${encodeURIComponent(n.node_id)}&touched=${touched ? 1 : 0}`);
        setKids(d.nodes || []);
      } catch (e) { setKids([]); }
      finally { setBusy(false); }
    }
    setOpen(o => !o);
  };
  const hasKids = isAct ? (n.objects || []).length > 0 : (n.kids || 0) > 0;
  return (
    <div>
      <div onClick={hasKids ? toggle : undefined}
        style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '3px 6px', paddingLeft: 6 + depth * 18,
          cursor: hasKids ? 'pointer' : 'default', borderRadius: 6 }}
        onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
        <span style={{ width: 12, color: '#94a3b8', fontSize: 11, flexShrink: 0 }}>
          {hasKids ? (busy ? '…' : open ? '▾' : '▸') : ''}
        </span>
        <span style={{ fontSize: 13, color: isAct ? 'rgb(var(--brand-700))' : '#0f172a', fontWeight: isAct ? 500 : 600 }}>
          {isAct && <span title="IMG activity" style={{ marginRight: 5 }}>⚙</span>}
          {n.text || n.node_id}
        </span>
        {n.extension && <span style={chip('#fef3c7', '#92400e')} title="IMG extension">{n.extension}</span>}
        <Counts n={n} />
      </div>
      {open && isAct && (
        <div style={{ paddingLeft: 36 + depth * 18, paddingBottom: 6 }}>
          {(n.objects || []).map(o => (
            <div key={o.objname} style={{ fontSize: 12, color: '#334155', padding: '2px 0' }}>
              <button onClick={() => onPick(o.objname)} title="Compare this object across systems"
                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'rgb(var(--brand-700))',
                  fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{o.objname}</button>
              {o.descr ? <span style={{ color: '#64748b' }}> — {o.descr}</span> : null}
              {o.tcode ? <span style={chip('#eef2ff', '#3730a3')}>{o.tcode}</span> : null}
            </div>
          ))}
        </div>
      )}
      {open && !isAct && kids && kids.map(k => (
        <TreeNode key={k.node_id} api={api} connId={connId} n={k} touched={touched} onPick={onPick} depth={depth + 1} />
      ))}
    </div>
  );
}

function TreeView({ api, connId, onPick, stamp }) {
  const [roots, setRoots] = useState(null);
  const [touched, setTouched] = useState(true);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let dead = false;
    setRoots(null); setErr('');
    api(`/api/sap/customizing/spro/tree?connectionId=${connId}&touched=${touched ? 1 : 0}`)
      .then(d => { if (!dead) setRoots(d.nodes || []); })
      .catch(e => { if (!dead) { setErr(e.message); setRoots([]); } });
    return () => { dead = true; };
  }, [api, connId, touched, stamp]);
  useEffect(() => {
    if (q.trim().length < 2) { setHits(null); return undefined; }
    const h = setTimeout(() => {
      api(`/api/sap/customizing/spro/search?connectionId=${connId}&q=${encodeURIComponent(q.trim())}`)
        .then(d => setHits(d.rows || [])).catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(h);
  }, [api, connId, q]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search SPRO path, activity, table, t-code…"
          style={{ ...INP, width: 340 }} />
        <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={touched} onChange={e => setTouched(e.target.checked)} />
          Only branches changed by customizing TRs
        </label>
      </div>
      {!!err && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
      {hits ? (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, maxHeight: 520, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={TH}>Object</th><th style={TH}>SPRO path</th><th style={TH}>IMG t-code</th><th style={{ ...TH, textAlign: 'right' }}>TR</th></tr></thead>
            <tbody>
              {hits.length === 0 && <tr><td style={TD} colSpan={4}>No match.</td></tr>}
              {hits.map((r, i) => (
                <tr key={i}>
                  <td style={TD}>
                    <button onClick={() => onPick(r.objname)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer',
                      color: 'rgb(var(--brand-700))', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.objname}</button>
                    {r.descr && <div style={{ fontSize: 11.5, color: '#64748b' }}>{r.descr}</div>}
                  </td>
                  <td style={TD}>
                    {r.path ? <>SPRO › {r.path}</> : <span style={{ color: '#94a3b8' }}>{r.act_text} (activity not in SAP Reference IMG)</span>}
                    {r.extension && <span style={chip('#fef3c7', '#92400e')}>{r.extension}</span>}
                  </td>
                  <td style={{ ...TD, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.tcode}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>{r.tr_count == null ? '—' : NF.format(r.tr_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '6px 4px', maxHeight: 620, overflow: 'auto' }}>
          {roots == null && <div style={{ padding: 10, fontSize: 12.5, color: '#64748b' }}>Loading…</div>}
          {roots && roots.length === 0 && !err && (
            <div style={{ padding: 10, fontSize: 12.5, color: '#64748b' }}>No SPRO structure cached yet — click “Pull SPRO from SAP”.</div>
          )}
          {(roots || []).map(n => (
            <TreeNode key={n.node_id} api={api} connId={connId} n={n} touched={touched} onPick={onPick} depth={0} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tabel → jalur SPRO ──────────────────────────────────────────────────────
function ObjectsView({ api, connId, onPick, stamp }) {
  const [q, setQ] = useState('');
  const [only, setOnly] = useState('all');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 200;
  useEffect(() => { setOffset(0); }, [q, only]);
  useEffect(() => {
    let dead = false;
    const h = setTimeout(() => {
      setLoading(true);
      api(`/api/sap/customizing/spro/objects?connectionId=${connId}&q=${encodeURIComponent(q.trim())}&only=${only}&limit=${limit}&offset=${offset}`)
        .then(d => { if (!dead) { setRows(d.rows || []); setTotal(d.total || 0); } })
        .catch(() => { if (!dead) { setRows([]); setTotal(0); } })
        .finally(() => { if (!dead) setLoading(false); });
    }, 250);
    return () => { dead = true; clearTimeout(h); };
  }, [api, connId, q, only, offset, stamp]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Table / view, description, SPRO path…"
          style={{ ...INP, width: 320 }} />
        <select value={only} onChange={e => setOnly(e.target.value)} style={INP}>
          <option value="all">All config objects</option>
          <option value="mapped">With SPRO path</option>
          <option value="unmapped">Without SPRO path</option>
        </select>
        <span style={{ fontSize: 12, color: '#64748b' }}>{loading ? 'Loading…' : `${NF.format(total)} objects`}</span>
      </div>
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, maxHeight: 620, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={TH}>Table / view</th><th style={{ ...TH, textAlign: 'right' }}>TR</th>
            <th style={TH}>SPRO path(s)</th><th style={TH} />
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.objname}>
                <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600 }}>{r.objname}</span>
                  {r.descr && <div style={{ fontSize: 11.5, color: '#64748b', whiteSpace: 'normal', maxWidth: 260 }}>{r.descr}</div>}
                </td>
                <td style={{ ...TD, textAlign: 'right' }}>{NF.format(r.tr || 0)}</td>
                <td style={TD}>
                  {r.path_count === 0 && (
                    <span style={{ color: '#94a3b8', fontSize: 12 }}>
                      Not found in SAP Reference IMG
                      {r.activities_outside ? ` (${r.activities_outside} activity outside the Reference IMG)` : ''}
                    </span>
                  )}
                  {r.paths.map((p, i) => (
                    <div key={i} style={{ fontSize: 12, padding: '1px 0' }}>
                      SPRO › {p.path}
                      {p.tcode && <span style={chip('#eef2ff', '#3730a3')}>{p.tcode}</span>}
                      {p.extension && <span style={chip('#fef3c7', '#92400e')}>{p.extension}</span>}
                    </div>
                  ))}
                  {r.path_count > r.paths.length && <div style={{ fontSize: 11.5, color: '#64748b' }}>+{r.path_count - r.paths.length} more</div>}
                  {r.via && <div style={{ fontSize: 11.5, color: '#64748b' }}>maintained via {r.via}</div>}
                </td>
                <td style={TD}>
                  <button onClick={() => onPick(r.objname)} style={{ ...BTN(false), padding: '0.25rem 0.6rem', fontSize: 11.5 }}>Compare</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > limit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(offset - limit, 0))} style={{ ...INP, background: '#fff' }}>‹</button>
          <span style={{ fontSize: 12, color: '#64748b' }}>{NF.format(offset + 1)}–{NF.format(Math.min(offset + limit, total))} / {NF.format(total)}</span>
          <button disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} style={{ ...INP, background: '#fff' }}>›</button>
        </div>
      )}
    </div>
  );
}

// ── Compare antar sistem ────────────────────────────────────────────────────
const STATUS_STYLE = {
  differs: ['#fef3c7', '#92400e', 'Different'],
  missing: ['#fee2e2', '#991b1b', 'Missing'],
  same: ['#dcfce7', '#166534', 'Same'],
};
const DEFAULT_IGNORE = ['ADRNR'];

function CompareView({ api, conns, defaultIds, pick }) {
  const [tab, setTab] = useState(pick || '');
  const [input, setInput] = useState(pick || '');
  const [sel, setSel] = useState(defaultIds);
  const [data, setData] = useState(null);
  const [bases, setBases] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ignore, setIgnore] = useState(DEFAULT_IGNORE);
  useEffect(() => { setSel(defaultIds); }, [defaultIds]);
  useEffect(() => { if (pick) { setInput(pick); setTab(pick); } }, [pick]);

  const load = useCallback(async (name) => {
    if (!name || !sel.length) { setData(null); return; }
    setErr('');
    try {
      const d = await api(`/api/sap/customizing/spro/cfg/compare?tabname=${encodeURIComponent(name)}&conns=${sel.join(',')}&ignore=${encodeURIComponent(ignore.join(','))}`);
      setData(d);
    } catch (e) { setErr(e.message); setData(null); }
  }, [api, sel, ignore]);
  useEffect(() => { setBases(null); load(tab); }, [tab, load]);

  const fetchSap = async (name) => {
    const nm = String(name || tab).trim().toUpperCase();
    if (!nm) return;
    if (!window.confirm(`Read the full content of ${nm} from ${sel.length} SAP system(s)? (max 5,000 rows each)`)) return;
    setBusy(true); setErr('');
    try {
      const r = await api('/api/sap/customizing/spro/cfg/fetch', { method: 'POST', body: JSON.stringify({ tabname: nm, conns: sel }) });
      if (r.kind !== 'table') {
        setBases({ kind: r.kind, list: r.bases || [] });
      } else {
        setBases(null);
        if (nm !== tab) setTab(nm); else await load(nm);
      }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const sysName = (id) => { const c = conns.find(x => x.id === id); return c ? `${c.sid}/${c.client}` : String(id); };
  const shownIds = data && data.systems ? data.systems.filter(s => s.rows != null && !s.error).map(s => s.connection_id) : [];

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <input value={input} onChange={e => setInput(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter') { setTab(input.trim()); } }}
          placeholder="Table name, e.g. T001W" style={{ ...INP, width: 200, fontFamily: 'ui-monospace, monospace' }} />
        <button onClick={() => setTab(input.trim())} style={BTN(false)}>Show cached</button>
        {isAdminUser() && <button onClick={() => fetchSap(input)} disabled={busy} style={BTN(true, busy)}>{busy ? 'Reading…' : 'Read from SAP'}</button>}
        <span style={{ fontSize: 12, color: '#64748b' }}>Systems:</span>
        {conns.map(c => (
          <label key={c.id} style={{ fontSize: 12.5, color: '#334155', display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={sel.includes(c.id)}
              onChange={e => setSel(s => e.target.checked ? [...s, c.id] : s.filter(x => x !== c.id))} />
            {c.sid}/{c.client}
          </label>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 10 }}>
        Reads the physical table with RFC_READ_TABLE in the logon client of each system (read-only, max 5,000 rows per system) and stores
        a snapshot here. Rows are matched by the table's key fields (client field excluded). Views and view clusters are resolved to
        their base tables first.
      </div>
      {!!err && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
      {bases && (
        <div style={{ padding: '0.6rem 0.9rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12.5, marginBottom: 10 }}>
          This is a {bases.kind}. Pick a base table to compare:
          {bases.list.length === 0 && ' (no base table found)'}
          {bases.list.map(b => (
            <button key={b} onClick={() => { setInput(b); fetchSap(b); }}
              style={{ ...BTN(false), padding: '0.2rem 0.55rem', marginLeft: 6, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{b}</button>
          ))}
        </div>
      )}
      {data && !data.cached && tab && (
        <div style={{ fontSize: 12.5, color: '#64748b' }}>No snapshot of {tab} yet — click “Read from SAP”.</div>
      )}
      {data && data.cached && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            {data.systems.map(s => (
              <div key={s.connection_id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.5rem 0.8rem', fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#0f172a' }}>{s.sid}/{s.client}</div>
                {s.error ? <div style={{ color: '#b91c1c' }}>{s.error.slice(0, 120)}</div>
                  : s.rows == null ? <div style={{ color: '#94a3b8' }}>not read yet</div>
                    : <div style={{ color: '#475569' }}>{NF.format(s.rows)} rows{s.truncated ? ' (capped — partial)' : ''} · {String(s.fetched_at || '').replace('T', ' ').slice(0, 16)}</div>}
              </div>
            ))}
            {['differs', 'missing', 'same'].map(k => (
              <div key={k} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.5rem 0.8rem', fontSize: 12 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: STATUS_STYLE[k][1] }}>{NF.format(data.counts[k] || 0)}</div>
                <div style={{ color: '#475569' }}>{STATUS_STYLE[k][2]} keys</div>
              </div>
            ))}
          </div>
          {Object.keys(data.field_counts || {}).length > 0 && (
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span>Fields that differ (untick to ignore, e.g. number-range fields like ADRNR):</span>
              {Object.entries(data.field_counts).sort((a, b) => b[1] - a[1]).map(([f, n]) => (
                <label key={f} style={{ display: 'flex', gap: 3, alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: 999, padding: '1px 8px' }}>
                  <input type="checkbox" checked={!ignore.includes(f)}
                    onChange={e => setIgnore(s => e.target.checked ? s.filter(x => x !== f) : [...s, f])} />
                  <span style={{ fontFamily: 'ui-monospace, monospace' }}>{f}</span> <span style={{ color: '#94a3b8' }}>{n}</span>
                </label>
              ))}
            </div>
          )}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, maxHeight: 560, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={TH}>Key ({(data.keys || []).join(' | ')})</th><th style={TH}>Status</th><th style={TH}>Field</th>
                {shownIds.map(id => <th key={id} style={TH}>{sysName(id)}</th>)}
              </tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td style={TD} colSpan={3 + shownIds.length}>All {NF.format(data.total_keys)} keys are identical across the selected systems.</td></tr>}
                {data.rows.map(r => {
                  const st = STATUS_STYLE[r.status];
                  const flds = r.fields.length ? r.fields : [''];
                  return flds.map((f, i) => (
                    <tr key={r.key + '|' + f}>
                      {i === 0 && <td style={{ ...TD, fontFamily: 'ui-monospace, monospace', fontSize: 12 }} rowSpan={flds.length}>{r.key}</td>}
                      {i === 0 && <td style={TD} rowSpan={flds.length}><span style={chip(st[0], st[1])}>{st[2]}</span></td>}
                      <td style={{ ...TD, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{f || '—'}</td>
                      {shownIds.map(id => (
                        <td key={id} style={{ ...TD, fontSize: 12, color: r.values[id] ? '#1e293b' : '#b91c1c' }}>
                          {r.values[id] ? (f ? String(r.values[id][f] ?? '') : 'present') : 'missing'}
                        </td>
                      ))}
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
          {data.rows_truncated && <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 6 }}>Showing the first 1,000 differing keys.</div>}
        </>
      )}
    </div>
  );
}

// ── Panel utama ─────────────────────────────────────────────────────────────
export default function SAPSproPanel({ api, connId, focus }) {
  // SAP GUI view hanya untuk role admin
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem('auth_user') || '{}').role === 'admin'; } catch { return false; } })();
  const [view, setView] = useState(focus ? 'recent' : (isAdmin ? 'gui' : 'tree'));
  const boxRef = useRef(null);
  // Tombol "Recent SPRO changes" di header: buka tab Recently changed dan gulir ke sini.
  useEffect(() => {
    if (!focus) return;
    setView('recent');
    const id = setTimeout(() => { if (boxRef.current) boxRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 150);
    return () => clearTimeout(id);
  }, [focus]);
  const [st, setSt] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState('');
  const [pick, setPick] = useState('');
  const [stamp, setStamp] = useState(0);
  const poll = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const d = await api(`/api/sap/customizing/spro/status?connectionId=${connId}`);
      setSt(d);
      return d;
    } catch (e) { setErr(e.message); return null; }
  }, [api, connId]);

  useEffect(() => { loadStatus(); return () => clearInterval(poll.current); }, [loadStatus]);

  const startPoll = useCallback(() => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const d = await loadStatus();
      if (d && !d.running) { clearInterval(poll.current); setSyncing(false); setStamp(x => x + 1); }
    }, 3000);
  }, [loadStatus]);

  useEffect(() => { if (st && st.running && !syncing) { setSyncing(true); startPoll(); } }, [st, syncing, startPoll]);

  const runSync = async () => {
    if (!window.confirm('Read the SPRO (Reference IMG) structure and IMG activity mapping from SAP? Takes about a minute.')) return;
    setErr(''); setSyncing(true);
    try {
      await api(`/api/sap/customizing/spro/sync?connectionId=${connId}`, { method: 'POST', body: '{}' });
      startPoll();
    } catch (e) { setErr(e.message); setSyncing(false); }
  };

  const conns = (st && st.conns) || [];
  const cur = conns.find(c => c.id === Number(connId));
  const defaultIds = [];
  if (cur) defaultIds.push(cur.id);
  ['Q01', 'P01'].forEach(sid => { const c = conns.find(x => x.sid === sid); if (c && !defaultIds.includes(c.id)) defaultIds.push(c.id); });
  const defKey = defaultIds.join(',');
  const defIds = useStableIds(defKey);

  const onPick = (name) => { setPick(name); setView('compare'); };
  const s = st && st.sync;

  return (
    <div ref={boxRef} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.9rem 1rem', marginBottom: 16, background: '#fff', scrollMarginTop: 80 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>SPRO navigator</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {st ? (
              <>
                {NF.format(st.objects_in_spro)} of {NF.format(st.objects_total)} config objects have an SPRO path
                {' · '}{NF.format(st.activities)} IMG activities
                {s && s.finished_at ? ` · last pull ${String(s.finished_at).replace('T', ' ').slice(0, 16)}` : ''}
                {s && s.status && s.status !== 'ok' && !st.running ? ` · ${s.status}: ${String(s.message || '').slice(0, 120)}` : ''}
                {st.running && s ? ` · ${s.message || 'running…'}` : ''}
              </>
            ) : 'Loading…'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Seg value={view} onChange={setView}
            items={[...(isAdmin ? [['gui', 'SAP GUI view']] : []), ['tree', 'SPRO tree'], ['recent', 'Recently changed'], ['objects', 'Table → SPRO path'], ['compare', 'Compare systems']]} />
          {isAdminUser() && <button onClick={runSync} disabled={syncing} style={BTN(true, syncing)}>{syncing ? 'Pulling…' : 'Pull SPRO from SAP'}</button>}
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
        padding: '0.45rem 0.7rem', marginBottom: 10, lineHeight: 1.5 }}>
        Paths come from SAP's own IMG tables (CUS_ACTOBJ → CUS_IMGACH → TNODEIMGR → TNODEIMG). Only IMG activities whose tables were
        changed by a customizing TR are shown as leaves — structure nodes are complete, activity leaves are not. Branches from IMG
        extensions (e.g. FIN_GL_IMG1, IS-MEDIA) are labelled; whether an extension is switched on is not checked. One table can sit
        under several activities — all are listed.
      </div>
      {!!err && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
      {isAdmin && view === 'gui' && <SAPSproGui api={api} connId={connId} conns={conns} stamp={stamp} />}
      {view === 'tree' && <TreeView api={api} connId={connId} onPick={onPick} stamp={stamp} />}
      {view === 'recent' && <RecentView api={api} connId={connId} onPick={onPick} />}
      {view === 'objects' && <ObjectsView api={api} connId={connId} onPick={onPick} stamp={stamp} />}
      {view === 'compare' && <CompareView api={api} conns={conns} defaultIds={defIds} pick={pick} />}
    </div>
  );
}

// ── Recently changed (table logging DBTABLOG, live) ─────────────────────────
const OPL = { I: 'insert', U: 'update', D: 'delete' };
const d8 = d => (d && d.length === 8) ? `${d.slice(6, 8)}.${d.slice(4, 6)}.${d.slice(0, 4)}` : d;
const t6 = t => (t && t.length === 6) ? `${t.slice(0, 2)}:${t.slice(2, 4)}` : '';
const SRC = {
  direct:    { label: 'Direct',          bg: '#fef3c7', fg: '#92400e' },
  tr_copy:   { label: 'Via TR (SCC1)',   bg: '#dbeafe', fg: '#1e40af' },
  tr_import: { label: 'Via TR (import)', bg: '#dbeafe', fg: '#1e40af' },
  system:    { label: 'System',          bg: '#f1f5f9', fg: '#64748b' },
};

function TrCheck({ api, connId, r }) {
  const [st, setSt] = useState(null);
  const run = async () => {
    setSt({ busy: true });
    try {
      setSt({ d: await api(`/api/sap/customizing/spro/recent-trcheck?connectionId=${connId}&tab=${encodeURIComponent(r.tabname)}&date=${r.date}&user=${encodeURIComponent(r.user)}`) });
    } catch (e) { setSt({ err: e.message }); }
  };
  if (!st) return <button onClick={run} style={{ ...BTN(false, false), padding: '2px 8px', fontSize: 11 }}>Recorded in TR?</button>;
  if (st.busy) return <span style={{ fontSize: 11, color: '#94a3b8' }}>Checking…</span>;
  if (st.err) return <span style={{ fontSize: 11, color: '#b91c1c' }} title={st.err}>Check failed</span>;
  const d = st.d;
  if (!d.keys) return <span style={{ fontSize: 11, color: '#94a3b8' }}>No keys in log</span>;
  const all = d.recorded === d.keys, none = d.recorded === 0;
  return (
    <div style={{ fontSize: 11 }}>
      <span style={chip(none ? '#fee2e2' : all ? '#dcfce7' : '#fef3c7', none ? '#991b1b' : all ? '#166534' : '#92400e')}>
        {none ? 'Not in any TR' : all ? 'Recorded in TR' : `Partly in TR (${d.recorded}/${d.keys} keys)`}
      </span>
      {d.trs.slice(0, 3).map(t => (
        <div key={t.trkorr} style={{ color: '#475569', marginTop: 2, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
          {t.parent || t.trkorr}{t.parent ? ` (task ${t.trkorr})` : ''} · {t.user} · {d8(t.date)} · {t.status === 'D' ? 'modifiable' : 'released'}
        </div>
      ))}
      {d.trs.length > 3 && <div style={{ color: '#94a3b8' }}>+{d.trs.length - 3} more TR</div>}
      {d.e071k_truncated && <div style={{ color: '#b45309' }}>E071K read limit reached — result may be incomplete</div>}
    </div>
  );
}

function RecentView({ api, connId, onPick }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [onlySpro, setOnlySpro] = useState(true);
  const [hideTech, setHideTech] = useState(true);
  const [q, setQ] = useState('');
  const [user, setUser] = useState('');
  const [src, setSrc] = useState('');

  const load = useCallback(async (fresh) => {
    setBusy(true); setErr('');
    try { setData(await api(`/api/sap/customizing/spro/recent?connectionId=${connId}&days=${days}${fresh ? '&fresh=1' : ''}`)); }
    catch (e) { setErr(e.message); setData(null); }
    finally { setBusy(false); }
  }, [api, connId, days]);
  useEffect(() => { load(false); }, [load]);

  const rows = ((data && data.rows) || []).filter(r =>
    (!hideTech || !r.technical) && (!onlySpro || r.spro.length) && (!user || r.user === user) &&
    (src ? (src === 'tr' ? /^tr_/.test(r.source) : r.source === src) : (!hideTech || r.source !== 'system')) &&
    (!q || r.tabname.includes(q.toUpperCase()) || r.spro.some(s => String(s.path || '').toUpperCase().includes(q.toUpperCase()) || String(s.act || '').toUpperCase().includes(q.toUpperCase()))));
  const users = [...new Set(((data && data.rows) || []).map(r => r.user))].sort();

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <select value={days} onChange={e => setDays(Number(e.target.value))} style={INP}>
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option><option value={180}>Last 180 days</option><option value={365}>Last 1 year</option>
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter table / SPRO path / activity" style={{ ...INP, flex: '1 1 220px' }} />
        <select value={user} onChange={e => setUser(e.target.value)} style={INP}>
          <option value="">All users</option>{users.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={src} onChange={e => setSrc(e.target.value)} style={INP}>
          <option value="">All sources</option><option value="direct">Direct only</option>
          <option value="tr">Via TR only</option><option value="system">System only</option>
        </select>
        <label style={{ fontSize: 12.5, color: '#334155' }}><input type="checkbox" checked={onlySpro} onChange={e => setOnlySpro(e.target.checked)} /> Only tables with an SPRO path</label>
        <label style={{ fontSize: 12.5, color: '#334155' }}><input type="checkbox" checked={hideTech} onChange={e => setHideTech(e.target.checked)} /> Hide technical tables</label>
        <button onClick={() => load(true)} disabled={busy} style={BTN(false, busy)}>{busy ? 'Reading…' : 'Refresh from SAP'}</button>
      </div>
      <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 8 }}>
        Source: table change log (DBTABLOG) read live from SAP — includes changes made directly in the client, not only via transport.
        Only tables with "Log data changes" enabled are recorded. Grouped per table · day · user.
        {data && ` · ${NF.format(data.raw_rows)} log rows · ${data.calls} RFC call(s) · ${(data.took_ms / 1000).toFixed(1)}s${data.cached ? ' · cached' : ''}`}
        {data && data.partial && <span style={{ color: '#b45309' }}> · partial: some days exceed the read limit</span>}
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
      {busy && !data && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>Reading change log from SAP…</div>}
      {data && (
        <div style={{ maxHeight: 520, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={TH}>Date</th><th style={TH}>Table</th><th style={TH}>SPRO path / IMG activity</th>
              <th style={TH}>User</th><th style={TH}>Source</th><th style={{ ...TH, textAlign: 'right' }}>Changes</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td style={TD} colSpan={6}><span style={{ color: '#94a3b8' }}>No logged changes match.</span></td></tr>}
              {rows.slice(0, 500).map(r => (
                <tr key={r.tabname + r.date + r.user}>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{d8(r.date)} <span style={{ color: '#94a3b8' }}>{t6(r.last)}</span></td>
                  <td style={TD}>
                    <button onClick={() => onPick(r.spro[0] ? r.spro[0].obj : r.tabname)} title="Compare this table across systems"
                      style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontWeight: 600, color: '#0369a1', fontSize: 12.5 }}>
                      {r.tabname}
                    </button>
                    {r.technical && <span style={chip('#f1f5f9', '#64748b')}>technical</span>}
                  </td>
                  <td style={TD}>
                    {r.spro.length === 0 ? <span style={{ color: '#94a3b8' }}>—</span> : r.spro.slice(0, 2).map((s, i) => (
                      <div key={i} style={{ fontSize: 12 }}>
                        <span style={{ color: '#475569' }}>{s.path}</span>
                        {s.act && <span style={{ color: '#0f172a', fontWeight: 600 }}>{s.path ? ' › ' : ''}{s.act}</span>}
                        {s.tcode && <span style={chip('#eef2ff', '#4338ca')}>{s.tcode}</span>}
                      </div>
                    ))}
                    {r.spro.length > 2 && <div style={{ fontSize: 11, color: '#94a3b8' }}>+{r.spro.length - 2} more</div>}
                  </td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.user}</td>
                  <td style={{ ...TD, minWidth: 170 }}>
                    <span style={{ ...chip((SRC[r.source] || SRC.direct).bg, (SRC[r.source] || SRC.direct).fg), marginLeft: 0 }}>{(SRC[r.source] || SRC.direct).label}</span>
                    <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>
                      {Object.entries(r.tcodes || {}).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k]) => k).join(', ')}
                    </div>
                    {r.source === 'direct' && <div style={{ marginTop: 3 }}><TrCheck api={api} connId={connId} r={r} /></div>}
                  </td>
                  <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {NF.format(r.n)}
                    <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{Object.entries(r.ops).map(([k, v]) => `${v} ${OPL[k] || k}`).join(' · ')}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 500 && <div style={{ fontSize: 11.5, color: '#94a3b8', padding: 8 }}>Showing 500 of {NF.format(rows.length)} — narrow the filter.</div>}
        </div>
      )}
    </div>
  );
}

// Array ber-identitas stabil selama isinya sama — supaya efek di CompareView
// tidak berjalan ulang di setiap render.
function useStableIds(key) {
  const ref = useRef({ key: null, ids: [] });
  if (ref.current.key !== key) ref.current = { key, ids: key ? key.split(',').map(Number) : [] };
  return ref.current.ids;
}
